import { __assign, __rest } from "tslib";
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import AutocompleteOptions from './AutocompleteOptions';
import Icon from './Icon';
import useFetch from '../hooks/useFetch';
import uniqueId from '../utilities/uniqueId';
import './Autocomplete.scss';
var MIN_QUERY_LENGTH = 2;
var CHANGE_INDEX = { Increment: 'ArrowDown', Decrement: 'ArrowUp' };
var DELIMITERS = ['Enter', 'Space'];
var CANCEL_NO_SAVE = ['Escape', 'Tab'];
var INPUT_SIZES = {
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
    FULL: 'full'
};
var Autocomplete = function (_a) {
    var _b;
    var className = _a.className, errorMessage = _a.errorMessage, formatOption = _a.formatOption, inputWidth = _a.inputWidth, isDisabled = _a.isDisabled, isRequired = _a.isRequired, label = _a.label, noResultsText = _a.noResultsText, onChange = _a.onChange, onClear = _a.onClear, onSelect = _a.onSelect, placeholder = _a.placeholder, selectedValueDisplayPropertyFallbacks = _a.selectedValueDisplayPropertyFallbacks, size = _a.size, statusIndicator = _a.statusIndicator, url = _a.url, value = _a.value, props = __rest(_a, ["className", "errorMessage", "formatOption", "inputWidth", "isDisabled", "isRequired", "label", "noResultsText", "onChange", "onClear", "onSelect", "placeholder", "selectedValueDisplayPropertyFallbacks", "size", "statusIndicator", "url", "value"]);
    var _c = useState(0), focusIndex = _c[0], setFocusIndex = _c[1];
    var _d = useState(''), query = _d[0], setQuery = _d[1];
    var _e = useState(value), inputValue = _e[0], setInputValue = _e[1];
    var containerRef = useRef(null);
    var inputRef = useRef(null);
    var _f = useFetch(url instanceof Function ? url(query) : url), isLoading = _f.isLoading, response = _f.response;
    var options = response !== null && response !== void 0 ? response : [];
    var isMenuOpen = query.length >= MIN_QUERY_LENGTH;
    var handleClickOutside = function (event) {
        var element = containerRef.current;
        if (element && !element.contains(event.target)) {
            setQuery('');
            setFocusIndex(0);
        }
    };
    useEffect(function () {
        if (isMenuOpen) {
            window.addEventListener('mousedown', handleClickOutside);
        }
        return function () {
            window.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);
    var reset = function () {
        setQuery('');
        setFocusIndex(0);
        if (!inputValue) {
            onChange === null || onChange === void 0 ? void 0 : onChange(null);
        }
    };
    var handleSelection = function (option) {
        var _a;
        reset();
        onChange === null || onChange === void 0 ? void 0 : onChange(option);
        setInputValue(option);
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(option);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var pressDelimiter = function (event) {
        event.preventDefault();
        var index = focusIndex === -1 ? 0 : focusIndex;
        return options && options[index] && isMenuOpen
            ? handleSelection(options[index])
            : null;
    };
    function pressArrowKey(event) {
        event.preventDefault();
        var nextIndex;
        if (event.key === CHANGE_INDEX.Decrement) {
            nextIndex = focusIndex <= 0 ? options.length - 1 : focusIndex - 1;
        }
        else {
            nextIndex = focusIndex >= options.length - 1 ? 0 : focusIndex + 1;
        }
        setFocusIndex(nextIndex);
    }
    var handleKeyDown = function (event) {
        if (isDisabled) {
            return;
        }
        if (DELIMITERS.includes(event.key)) {
            pressDelimiter(event);
        }
        if (Object.values(CHANGE_INDEX).includes(event.key)) {
            pressArrowKey(event);
        }
        if (CANCEL_NO_SAVE.includes(event.key)) {
            reset();
        }
    };
    var handleClear = function () {
        var _a;
        if (isDisabled) {
            return;
        }
        onClear === null || onClear === void 0 ? void 0 : onClear();
        setInputValue(null);
        onChange === null || onChange === void 0 ? void 0 : onChange(null);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var handleChange = function (event) {
        if (isDisabled) {
            return;
        }
        setFocusIndex(0);
        setInputValue(null);
        setQuery(event.target.value);
        if (event.target.value === '') {
            onChange === null || onChange === void 0 ? void 0 : onChange(null);
        }
    };
    var autoCompleteSearchClass = classnames('Autocomplete__search', "Autocomplete__search--".concat(size), {
        'Autocomplete__search--isInvalid': Boolean(errorMessage)
    });
    var containerClass = classnames('Autocomplete', "Autocomplete--".concat(inputWidth, "Width"), { 'Autocomplete--isDisabled': isDisabled }, className);
    var inputId = useRef(uniqueId('input'));
    /**
     * This component takes an object as the value, but uses one of this object's properties
     * to display the selected value. The shape of the data value is not standardized and the
     * preferred display property is not always required in the backend model (e.g. user name).
     * So we allow the consuming code to pass an array of properties ordered by preference.
     */
    var displayProperty = (_b = selectedValueDisplayPropertyFallbacks === null || selectedValueDisplayPropertyFallbacks === void 0 ? void 0 : selectedValueDisplayPropertyFallbacks.find(function (property) {
        return property ? inputValue === null || inputValue === void 0 ? void 0 : inputValue[property] : '';
    })) !== null && _b !== void 0 ? _b : '';
    useEffect(function () {
        setInputValue(value);
    }, [value]);
    return (React.createElement("div", __assign({}, props, { className: containerClass }),
        label ? (React.createElement("label", { className: 'Autocomplete__label', htmlFor: inputId.current },
            isRequired ? React.createElement("span", { "aria-hidden": 'true' }, "* ") : null,
            label)) : null,
        React.createElement("div", { className: autoCompleteSearchClass },
            React.createElement(Icon, { className: 'Autocomplete__icon', size: size === INPUT_SIZES.SMALL ? 'medium' : 'large', type: 'search' }),
            React.createElement("input", { className: 'Autocomplete__searchInput', "data-testid": 'autocompleteInput', disabled: isDisabled, id: inputId.current, onChange: handleChange, onKeyDown: handleKeyDown, placeholder: placeholder, ref: inputRef, required: isRequired, value: (inputValue === null || inputValue === void 0 ? void 0 : inputValue[displayProperty]) || query }),
            statusIndicator || null,
            inputValue && !isDisabled ? (React.createElement(Icon, { className: 'Autocomplete__clearIcon', onClick: handleClear, type: 'close' })) : null),
        React.createElement(AutocompleteOptions, { focusIndex: focusIndex, formatOption: formatOption, isLoading: isLoading, minQueryLength: MIN_QUERY_LENGTH, noResultsText: noResultsText, onHover: setFocusIndex, onSelect: handleSelection, options: options, query: query }),
        errorMessage ? (React.createElement("div", { className: 'Autocomplete__errorMessage', role: 'alert' }, errorMessage)) : null));
};
export default Autocomplete;
Autocomplete.defaultProps = {
    inputWidth: INPUT_SIZES.FULL,
    isDisabled: false,
    isRequired: false,
    noResultsText: 'No results, please try another query',
    onBlur: function () { },
    onChange: function () { },
    selectedValueDisplayPropertyFallbacks: ['name', 'id'],
    size: INPUT_SIZES.MEDIUM
};
Autocomplete.propTypes = {
    /** Class name for the container div */
    className: PropTypes.string,
    /** Error message to display  */
    errorMessage: PropTypes.node,
    /** Returns an element that renders a single autocomplete option */
    formatOption: PropTypes.func.isRequired,
    /** The width of the text input */
    inputWidth: PropTypes.oneOf(Object.values(INPUT_SIZES)),
    /** True when the input is disabled */
    isDisabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    /** Label for the search input */
    label: PropTypes.node,
    /** The text to display when there are no matching results returned from the server */
    noResultsText: PropTypes.string,
    onBlur: PropTypes.func,
    /** Callback for input value change */
    onChange: PropTypes.func,
    /** Callback for clearing the input */
    onClear: PropTypes.func,
    /** Callback for selecting an option */
    onSelect: PropTypes.func,
    /** Text to display in input before a selection is made */
    placeholder: PropTypes.string,
    /** The first item/property that has a truthy value is used as the display property. */
    selectedValueDisplayPropertyFallbacks: PropTypes.arrayOf(PropTypes.string),
    /** The input height */
    size: PropTypes.oneOf(Object.values(INPUT_SIZES)),
    /** The url for the autocomplete endpoint */
    url: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    value: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired
    })
};
//# sourceMappingURL=Autocomplete.js.map